import {
  faCopy,
  faFileLines,
  faPlus,
} from "@fortawesome/pro-regular-svg-icons";
import { Navigate } from "react-router-dom";

import { CasePageLayout } from "src/cases/CasePageLayout";
import { useCaseTemplatesWithVersions } from "src/cases/queries/templates";
import { useAddTemplate } from "src/cases/templates/AddTemplateDropdown";
import { EmptyState } from "src/design-system/EmptyState";
import { Skeleton } from "src/design-system/Skeleton";
import { useCapabilities } from "src/hooks/useCapabilities";
import { WhitePane } from "src/jobs/common/WhitePane";
import {
  DashboardPageParams,
  getUrlToCaseQueuePage,
  getUrlToCaseTemplatesPage,
  getUrlToCaseTemplateVersionsPage,
} from "src/router/urls";
import { useParamsDecode } from "src/utils/useParamsDecode";

/**
 * Index route for `/cases/templates` (feature-flagged path):
 * - Redirects to the first template's versions page when templates exist
 * - Shows an empty state with "Add template" action when no templates exist
 */
export const CaseTemplatesRedirect: React.FC = () => {
  const { orgId, wsId } = useParamsDecode<DashboardPageParams>();
  const { caseTemplates } = useCapabilities();
  const {
    data: templates,
    isLoading,
    isError,
  } = useCaseTemplatesWithVersions();

  const { openFromScratch, openFromTemplate, modals } = useAddTemplate();

  if (isLoading) {
    return (
      <div className="flex h-full items-center justify-center p-10">
        <Skeleton height="h-6" width="w-40" />
      </div>
    );
  }

  if (isError) {
    return <Navigate replace to={getUrlToCaseQueuePage({ orgId, wsId })} />;
  }

  const firstTemplate = templates?.[0];
  if (firstTemplate) {
    return (
      <Navigate
        replace
        to={getUrlToCaseTemplateVersionsPage(orgId, wsId, firstTemplate.id)}
      />
    );
  }

  return (
    <CasePageLayout
      breadcrumbHref={getUrlToCaseTemplatesPage(orgId, wsId)}
      breadcrumbLabel="Case templates"
    >
      <WhitePane>
        <EmptyState
          actionButton={
            caseTemplates.canCreate
              ? [
                  {
                    dataLoc: "create-from-scratch-empty",
                    icon: faPlus,
                    label: "From scratch",
                    onClick: openFromScratch,
                  },
                  {
                    dataLoc: "create-from-template-empty",
                    icon: faCopy,
                    label: "From a template",
                    onClick: openFromTemplate,
                  },
                ]
              : false
          }
          dataLoc="case-templates-empty-state"
          description={
            caseTemplates.canCreate
              ? "Create a Case template to define the structure and workflow for your Cases."
              : "There are no Case templates in this workspace yet. Contact your administrator to create one."
          }
          headline="No templates yet"
          icon={faFileLines}
        />
      </WhitePane>
      {modals}
    </CasePageLayout>
  );
};
